// These global reactive vars only exist for cases where shared
// reactive state across multiple components is required,
// use this pattern sparingly!

import { reactive, ref } from 'vue';

// -----------------------------------------------------------------------------
// Cloud-mode detection.
//
// The same webui codebase ships in two deployments:
//   1. On-prem: served by Caddy alongside the Python backend. All API + WS
//      paths are relative to the same origin (`state.devRedirect = ''`).
//   2. Cloud: served by Cloudflare Pages, talks to a Cloudflare Worker for
//      API + WS. We detect this by hostname so no separate build is needed.
//
// In cloud mode:
//   * `state.devRedirect` is set to the Worker's absolute origin so that
//     every existing `state.devRedirect + '/api/...'` call site Just Works.
//   * `state.wsBase` is set to the Worker's `wss://` origin and the small
//     number of WS-constructing sites prefer it when present.
//
// Override at runtime by setting `window.__CPRO_CLOUD__ = false` (or true)
// before the app boots — handy for testing the cloud build from localhost.
// -----------------------------------------------------------------------------
const _hostIsCloud =
  typeof window !== 'undefined' &&
  /\.pages\.dev$|^canvaspro-cloud\./.test(window.location.host);
export const IS_CLOUD_MODE =
  typeof window !== 'undefined' && typeof window.__CPRO_CLOUD__ !== 'undefined'
    ? !!window.__CPRO_CLOUD__
    : _hostIsCloud;

const CLOUD_WORKER_HTTPS = 'https://canvaspro-state-stream.canvaspro.workers.dev';
const CLOUD_WORKER_WSS = 'wss://canvaspro-state-stream.canvaspro.workers.dev';

export async function getSubKey(key, subKey) {
  // Returns subkey from local cache, if not available with fetch from server
  return deluge[key][subKey];
}

export const state = reactive({
  $ident: '', // Omni identity uid
  upload_files: [], // Files scheduled to upload
  upload_current_index: 0,
  upload_progress: 0,
  upload_total: 0,
  upload_current_filename: '',
  uploading: false, // Upload in progress flag
  upload_start_time: 0, // Timestamp when current file started
  upload_bytes_total: 0, // Total bytes of current file
  upload_bytes_done: 0, // Bytes uploaded so far
  upload_eta: '', // Formatted ETA string (e.g., "2m 30s")
  upload_session_uid: null, // Current upload session UID for cancellation
  upload_cancel_requested: false, // Flag to signal upload cancellation
  upload_uploading: false, // Legacy - kept for compatibility
  current_asset_group: null, // Current asset group context for uploads
  selectedq: undefined, // OnAir Drag & Drop
  selecteds: undefined, //  OnAir Drag & Drop
  nested_queues: {}, // Nested Tracker
  cue_drag_active: false,
  channels_cache: {},
  q_is_not_draggable: undefined, // OnAir Drag & Drop
  qind: -1, // OnAir Drag & Drop
  theme: 'light',
  current_user: undefined,
  is_omni: false,
  is_orb: false, // true when product is orb (vs ventuz-based omni/one)
  selected_frame: 1, // OnAir persistent frames
  select_scene_drag: -1, // Scene DID for OnAir cue function
  cued_dids: {},
  notifications: [],
  ctrl_down: 0,
  shift_down: 0,
  console_history: [],
  console_open: false,
  beta: localStorage.getItem('enable_beta', 'true'),
  cueTakeUI: {
    sceneDragActive: false,
    sceneDraggedDID: null,
    sceneDropped: false,
  },
  // In cloud mode this becomes the Worker's absolute origin; otherwise
  // it stays as the on-prem behavior (empty in prod, '/dev' in vite dev).
  devRedirect: IS_CLOUD_MODE
    ? CLOUD_WORKER_HTTPS
    : import.meta.env.DEV
      ? '/dev'
      : '',
  // Absolute WS origin used by the `/deluge_core` connection and any other
  // WebSocket created from this app. `null` in on-prem mode means "construct
  // from window.location" as before.
  wsBase: IS_CLOUD_MODE ? CLOUD_WORKER_WSS : null,
  channelOnAirEditMode: false,
  sceneConditionSummary: false,
  sceneConditionSummaryTask: undefined,
  sceneConditionState: {},
  sceneConditionSummaryModal: false,
  ctrl_down: false,
  shift_down: false,
  open: false, // for AssetsTemplatesDropdownOverlay and AssetsTemplatesDropdown
  item: false, // for AssetsTemplatesDropdownOverlay and AssetsTemplatesDropdown
  isSearching: false, // for AssetsTemplatesDropdownOverlay loading state
  videoSource: null,
  isPlaying: false,
  streamMode: 'webrtc', // 'webrtc', 'mse', or 'mjpeg' - indicates current preview stream mode
});

export const deluge_sock = ref(undefined);

// Pre-seed with the keys components touch unguardedly so the brief race
// between component render and the /deluge_core WS GET response doesn't
// throw `Cannot read properties of undefined (reading '<some-uid>')`.
// On-prem this race is invisible (Redis is local); cloud takes ~50-300ms
// for the DO round-trip on first connection. Components elsewhere assume
// these objects exist and index into them directly.
export const deluge = reactive({
  tags: {},
  assets: {},
  templates: {},
  channels: {},
  items: {},
  queues: {},
  scenes: {},
  frames: {},
  players: {},
  connected_players: {},
  available_players: {},
  apps: {},
  triggers: {},
  all_conditions: {},
  config: {},
  hw: {},
  ingest: {},
  panels: {},
  panels_meta: {},
  pinned_svars: {},
  volume_svars: {},
  ndi: {},
  // Default role row so App.vue's `deluge.roles[role_id].permissions.includes`
  // doesn't throw during the WS-load race. WS GET fills the real roles in.
  roles: { 1: { id: 1, name: 'Admin', permissions: [], uid: 'role-admin' } },
  feature_flags: {},
  info: { product_name: '', version_number: '' },
  latest_log: { latest: { msg: '', bg: 'transparent', fg: 'transparent', level: 'info' } },
});

document.addEventListener('keydown', (e) => {
  if (e.key === '`') {
    state.console_open = !state.console_open;
  }
});
document.addEventListener('keyup', (e) => {
  if (e.key === 'Control') {
    state.ctrl_down = Date.now() - 500;
  }
  if (e.key === 'Shift') {
    state.shift_down = Date.now() - 500;
  }
});
